import { useState } from "react";
import { Search, ClipboardPlus, Check, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { BigButton, inputCls } from "@/components/ui-kit";

export default function ERxDialog({ open, onClose, onUse }) {
  const { t, view, online, lang } = useApp();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    try { setRows((await api.get("/rx/search", { params: { q: q.trim() } })).data); } catch { toast.error(t("need_internet")); }
    setBusy(false);
  };
  const [vsc, setVsc] = useState("");
  const [ver, setVer] = useState(null);
  const verify = async () => {
    if (vsc.trim().length < 8) return;
    setBusy(true);
    try { setVer((await api.get("/rx/verify", { params: { vsc: vsc.trim() } })).data); } catch { toast.error(t("need_internet")); }
    setBusy(false);
  };
  const products = Object.values(view.products).filter((p) => !p.deleted);
  const match = (drug) => { const d = drug.toLowerCase(); return products.find((p) => p.name.toLowerCase().includes(d) || (p.active_ingredient || "").toLowerCase() && d.includes(p.active_ingredient.toLowerCase()) || d.includes(p.name.toLowerCase().split(" ")[0])); };
  const use = (rx) => {
    const found = rx.items.map((i) => ({ item: i, product: match(i.drug) }));
    onUse({ rx, products: found.filter((f) => f.product).map((f) => f.product), missing: found.filter((f) => !f.product).map((f) => f.item.drug) });
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dlg-full p-0 bg-[#F0F0F0]" data-testid="erx-dialog">
        <div className="max-w-3xl mx-auto w-full p-4 space-y-3">
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><ClipboardPlus className="w-6 h-6 text-[#C0392B]" />{t("erx_title")}</DialogTitle>
          <p className="text-sm text-[#555]">{t("erx_hint")}</p>
          <div className="flex gap-2">
            <input dir="auto" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder={t("erx_search_ph")} className={inputCls} data-testid="erx-search-input" autoFocus />
            <BigButton onClick={search} disabled={!online || busy} data-testid="erx-search-btn"><Search className="w-5 h-5" />{t("search")}</BigButton>
          </div>
          <div className="rounded-xl border border-[#CCCCCC] bg-white p-3 space-y-2" data-testid="vsc-verify-box">
            <div className="flex items-center gap-2 text-sm font-bold"><ShieldCheck className="w-5 h-5 text-[#0B2A5B]" />{t("vsc_verify_title")}</div>
            <div className="flex gap-2">
              <input dir="ltr" value={vsc} onChange={(e) => { setVsc(e.target.value.toUpperCase()); setVer(null); }} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="VSC-XXXX-XXXX-XXXX-XXXX" className={`${inputCls} font-mono`} data-testid="vsc-input" />
              <BigButton onClick={verify} disabled={!online || busy} data-testid="vsc-verify-btn"><ShieldCheck className="w-5 h-5" />{t("vsc_verify")}</BigButton>
            </div>
            {ver && (
              <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${ver.result === "valid" ? "bg-[#E3F6EC] text-[#14532D]" : ver.result === "altered" ? "bg-[#FDF5F5] text-[#C7353B]" : "bg-[#FFF4E5] text-[#9A3412]"}`} data-testid={`vsc-result-${ver.result}`}>
                {ver.result === "valid" ? <ShieldCheck className="w-5 h-5 shrink-0" /> : <ShieldAlert className="w-5 h-5 shrink-0" />}
                <div>
                  <b>{t(`vsc_${ver.result}`)}</b>
                  {ver.rx && <div className="text-xs mt-1">℞ <span className="font-mono" dir="ltr">{ver.rx.code}</span> · {ver.rx.patient_name} · {ver.rx.clinic_name} · {ver.rx.doctor_name} · <span dir="ltr">{fmtDate(ver.rx.issued_at, lang)}</span> · {t(`erx_${ver.rx.status}`)}{ver.rx.dispensed_by && ` · ${ver.rx.dispensed_by}`}</div>}
                  {ver.rx && ver.result === "valid" && <BigButton variant="green" onClick={() => use(ver.rx)} className="mt-2" data-testid="vsc-use-btn"><ClipboardPlus className="w-5 h-5" />{t("erx_dispense")}</BigButton>}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2" data-testid="erx-results">
            {rows.map((rx) => (
              <div key={rx.code} className="rounded border border-[#CCCCCC] bg-white p-3" data-testid="erx-row">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-mono font-bold text-[#C0392B]" dir="ltr">℞ {rx.code}</span>{rx.vsc && <span className="font-mono text-[10px] text-[#0B2A5B]" dir="ltr" data-testid="erx-row-vsc">{rx.vsc}</span>}<span className="font-bold">{rx.patient_name}</span><span className="text-[#666]">{rx.patient_file_no}</span>
                  <span className="text-[#666]">· {rx.clinic_name} · {rx.doctor_name} · <span dir="ltr">{rx.date}</span></span>
                  <span className={`ms-auto px-2 py-0.5 rounded text-white text-xs font-bold ${rx.status === "dispensed" ? "bg-[#1F9D55]" : "bg-[#E67E22]"}`} data-testid="erx-status">{t(`erx_${rx.status}`)}{rx.dispensed_by && ` · ${rx.dispensed_by}`}</span>
                </div>
                {rx.diagnosis && <div className="text-xs text-[#555] mt-1">{t("diagnosis")}: {rx.diagnosis}</div>}
                <ol className="list-decimal ps-5 text-sm mt-1">{rx.items.map((i, k) => { const p = match(i.drug); return <li key={k}>{i.drug} — <span dir="ltr">{i.dose}</span> {i.duration} {p ? <Check className="inline w-4 h-4 text-[#1F9D55]" /> : <span className="text-xs text-[#C7353B]">({t("erx_no_stock")})</span>}</li>; })}</ol>
                <BigButton variant="green" onClick={() => use(rx)} className="mt-2 w-full" data-testid="erx-use-btn"><ClipboardPlus className="w-5 h-5" />{t("erx_dispense")}</BigButton>
              </div>
            ))}
            {!rows.length && <p className="text-sm text-muted-foreground">{t("erx_empty")}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
